<template>
  <div ref="activatorElem">
    <edit-field
        ref="EDIT_FIELD"
        :value="editValue"
        :periods="periods"
        :edit-field-info="cloneEditFieldInfo"
        :required="required"
        :disabled="disabled"
        :context-id="contextId ? contextId : 0"
        :parent-focus="showMenu"
        :hide-label="hideLabel"
        :show-missing="showMissing"
        @updated=setUpdated
        @error=setError>
    </edit-field>
  </div>
  <v-menu
      v-if="activator && !disabled"
      ref="MENU"
      v-model="showMenu"
      :close-on-content-click="false"
      :nudge-right="40"
      transition="scale-transition"
      offset-y
      max-width="290px"
      min-width="290px"
      :activator="activator">
    <v-locale-provider locale="fr">
      <v-date-picker
          v-model="pickerComputed"
          locale="fr-in"
          no-title
          :min="pickerMin"
          :max="pickerMax"
          :allowed-dates="inPickerPeriod"
          reactive
          @update:model-value="showMenu = false">
      </v-date-picker>
    </v-locale-provider>
  </v-menu>
</template>

<script lang="ts">
import {FieldError, FieldInfo, FieldStatus} from '@/framework/base/components/FieldInfo';
import {DateFormat, Period} from '@/framework/base/helpers/DateFormat';
import validator, {rules, RULES_FRENCH_LABEL} from '@/generated/validators/ValidationRules';
import EditField from '@/framework/base/components/EditField.vue';
import {defineComponent, type PropType} from "vue";

export default defineComponent({
  components: {EditField},
  props: {
    value: {
      type: [String, Date],
      required: true
    },
    periods: {
      type: Array as PropType<Period[]>,
      required: true
    },
    editFieldInfo: {
      type: Object as PropType<FieldInfo>,
      required: true
    },
    required: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    hideLabel: {
      type: Boolean
    },
    contextId: {
      type: Number
    },
    showMissing: {
      type: Boolean
    }
  },
  data() {
    const cloneEditFieldInfo = FieldInfo.fromJson(this.editFieldInfo);
    return {
      cloneEditFieldInfo,
      dateString: "",
      initValue: "",
      lastValue: "",
      editing: false,
      editError: false,
      showMenu: false,
      status: new FieldStatus(false, false, 0),
      activator: null,
    };
  },
  computed: {
    pickerComputed: {
      get(): string {
        this.setEditError(false);
        let pickerValue = DateFormat.toPicker(this.editValue);
        if (pickerValue) {
          if (!this.valid(pickerValue, this.periods)) {
            pickerValue = this.pickerMin;
            this.setEditError(true);
          } else if (this.editValue) {
            if (this.value != this.editValue) {
              this.setEditValue(DateFormat.toEdit(this.value.toString()));
            } else {
              this.setUpdated(this.editValue);
            }
          }
        }
        return pickerValue;
      },
      set(value: string) {
        this.editing = true;
        this.editError = false;
        this.dateString = DateFormat.toEdit(value);
        this.signalError(false);
        this.setUpdated(this.dateString);
      }
    },
    editValue(): string {
      this.setDateString();
      return this.dateString;
    },
    pickerMin(): string {
      let min = '2099-12-31';
      if (this.periods && this.periods.length > 0) {
        for (let i = 0; i < this.periods.length; i++) {
          if (!this.periods[i].min) {
            min = '1900-01-01';
          } else if (DateFormat.toNumber(this.periods[i].min) < DateFormat.toNumber(min)) {
            min = DateFormat.toPicker(this.periods[i].min);
          }
        }
      } else {
        min = '1900-01-01';
      }
      return min;
    },
    pickerMax(): string {
      let max = '1900-01-01';
      if (this.periods && this.periods.length > 0) {
        for (let i = 0; i < this.periods.length; i++) {
          if (!this.periods[i].max) {
            max = '2099-12-31';
          } else if (DateFormat.toNumber(this.periods[i].max) > DateFormat.toNumber(max)) {
            max = DateFormat.toPicker(this.periods[i].max);
          }
        }
      } else {
        max = '2099-12-31';
      }
      return max;
    },
  },
  created(): void {
    if (this.value) {
      this.initValue = DateFormat.toEdit(this.value.toString());
    }
    this.editValue = this.initValue;
    this.lastValue = this.initValue;
    this.cloneEditFieldInfo.mask = this.mask;
    this.cloneEditFieldInfo.focusValidator = this.focusValidator;
    this.cloneEditFieldInfo.liveValidator = null;
    this.cloneEditFieldInfo.valid = this.valid;
  },
  mounted() {
    this.$nextTick(() => {
      this.activator = this.$refs.activatorElem;
    });
  },
  methods: {
    setDateString(): void {
      if (this.value && this.value.toString() != this.dateString) {
        this.dateString = DateFormat.toEdit(this.value.toString());
      }
    },
    setEditValue(value: string) {
      this.editValue = value;
    },
    setEditError(value: boolean) {
      this.editError = value;
    },
    focusValidator(value: string): validator | null {
      return new validator(rules.dateFormatValid, RULES_FRENCH_LABEL.text.dateFormatValid.error);
      ;
    },
    mask(value: string): string {
      return 'NN/NN/NNNN';
    },
    setUpdated(value: string): void {
      this.editValue = value;
      if (!this.status.error) {
        this.status.updated = value != this.initValue;
        if (value != this.lastValue) {
          this.lastValue = value;
          this.$emit("updated", DateFormat.toIso(value));
        }
      }
    },
    inPickerPeriod(val: string): string | null {
      return DateFormat.toPicker(this.checkAllowedDate(DateFormat.toEdit(val), this.periods));
    },
    valid(value: string, periods: Period[]): boolean {
      return !!this.checkAllowedDate(value, periods);
    },
    checkAllowedDate(val: string, periods: Period[]): string | null {
      if (!periods || periods.length == 0) {
        return val;
      }
      let res = null;
      if (DateFormat.toPicker(val)) {
        const valAsNumber = DateFormat.toNumber(val);

        for (let i = 0; i < periods.length; i++) {
          if (periods[i].min && periods[i].max) {
            if (valAsNumber >= DateFormat.toNumber(periods[i].min) && valAsNumber <= DateFormat.toNumber(periods[i].max)) {
              res = val
            }
          } else if (periods[i].min && !periods[i].max) {
            if (valAsNumber >= DateFormat.toNumber(periods[i].min)) {
              res = val;
            }
          } else if (!periods[i].min && periods[i].max) {
            if (valAsNumber <= DateFormat.toNumber(periods[i].max)) {
              res = val;
            }
          } else {
            res = val;
          }
        }
      }

      return res;
    },
    setError(value: FieldError): void {
      if (value.error == false) {
        value.error = false;
      }
      this.signalError(value.error);
    },
    signalError(value: boolean): void {
      if (this.status.error != value) {
        this.status.error = value;
        this.$emit("error", new FieldError(0, '', value));
      }
    },
    missing(): boolean {
      return this.required && (!this.value || this.value == '');
    },
    touch(): void {
      // @ts-expect-error : generated method
      this.$refs.EDIT_FIELD.touch();
    },
    setFocus(): void {
      // @ts-expect-error : generated method
      this.$refs.EDIT_FIELD.focus();
    }
  }
})

</script>
